import { CROP_TYPE_FCPO, FIELD_VERIFICATION_FILTER, FieldVerification, FieldVerificationFilter } from "models";

/**
 * Parsed filter criteria for crop filtering
 */
export type FilterCriteria = {
  cropName?: string;
  cropType?: CROP_TYPE_FCPO;
  year?: number;
};

/**
 * Parses a filter string into structured criteria
 * @param filter - Filter string in format "crop-Name|year-2026|cropType-COVER"
 * @returns Parsed filter criteria
 */
export const parseFilter = (filter: string): FilterCriteria => {
  const parts = filter.split("|");
  const criteria: FilterCriteria = {};

  parts.forEach((part) => {
    if (part.startsWith("crop-") && !part.startsWith("cropType-")) {
      const cropName = part.replace("crop-", "");
      if (cropName) {
        criteria.cropName = cropName;
      }
    } else if (part.startsWith("cropType-")) {
      const cropTypeValue = part.replace("cropType-", "");
      // Validate that it's a valid CROP_TYPE_FCPO enum value
      if (Object.values(CROP_TYPE_FCPO).includes(cropTypeValue as CROP_TYPE_FCPO)) {
        criteria.cropType = cropTypeValue as CROP_TYPE_FCPO;
      }
    } else if (part.startsWith("year-")) {
      const yearValue = parseInt(part.replace("year-", ""), 10);
      if (!Number.isNaN(yearValue) && yearValue > 0) {
        criteria.year = yearValue;
      }
    }
  });

  return criteria;
};

/**
 * Type for crop data stored in the field crops map
 */
export type FieldCropData = {
  cropName: string;
  year?: number;
  cropType: CROP_TYPE_FCPO;
};

/**
 * Checks if a field matches the given filter criteria
 * @param field - Field to check
 * @param criteria - Filter criteria to match against
 * @param fieldCropsMap - Map of fieldId to crops data
 * @returns True if field matches criteria
 */
export const matchesFilterCriteria = (field: FieldVerification, criteria: FilterCriteria, fieldCropsMap: Map<string, FieldCropData[]> | null): boolean => {
  if (!fieldCropsMap) return false;

  const fieldCrops = fieldCropsMap.get(field.id) || [];

  // If no valid criteria were parsed, don't filter
  if (!criteria.cropName && !criteria.cropType && !criteria.year) {
    return true;
  }

  // Check if there's a single crop that matches ALL criteria
  return fieldCrops.some((crop) => {
    let matches = true;
    if (criteria.cropName !== undefined) {
      matches = matches && crop.cropName === criteria.cropName;
    }
    if (criteria.cropType !== undefined) {
      matches = matches && crop.cropType === criteria.cropType;
    }
    if (criteria.year !== undefined) {
      matches = matches && crop.year === criteria.year;
    }
    return matches;
  });
};

/**
 * Filters fields based on the selected filter
 * @param fields - Array of fields to filter
 * @param filter - Filter string or enum value
 * @param fieldCropsMap - Map of fieldId to crops data for crop-based filtering
 * @returns Filtered array of fields
 */
export const filterFields = (
  fields: FieldVerification[],
  filter: FieldVerificationFilter | undefined,
  fieldCropsMap: Map<string, FieldCropData[]> | null
): FieldVerification[] => {
  if (!filter || filter === FIELD_VERIFICATION_FILTER.allFields) return fields;

  return fields.filter((field) => {
    switch (filter) {
      case FIELD_VERIFICATION_FILTER.pendingVerification:
        return field.verifications.some(({ verified }) => verified === null);

      case FIELD_VERIFICATION_FILTER.pendingCrops:
        return field.pendingPractices.crops > 0;

      case FIELD_VERIFICATION_FILTER.pendingFertilizers:
        return field.pendingPractices.fertilizers > 0;

      case FIELD_VERIFICATION_FILTER.pendingTillageSeeding:
        return field.pendingPractices.tillageSeeding > 0;

      // Editing request filters for crops (crop-level)
      case FIELD_VERIFICATION_FILTER.hasCropEditingRequests: {
        const { cropLevel } = field.editingRequests;
        return cropLevel.open + cropLevel.toReview + cropLevel.reviewed > 0;
      }

      case FIELD_VERIFICATION_FILTER.openEditingRequests:
        return field.editingRequests.cropLevel.open > 0;

      case FIELD_VERIFICATION_FILTER.toReviewEditingRequests:
        return field.editingRequests.cropLevel.toReview > 0;

      case FIELD_VERIFICATION_FILTER.reviewedEditingRequests:
        return field.editingRequests.cropLevel.reviewed > 0;

      default: {
        // Handle crop, year, and crop type filtering
        const criteria = parseFilter(filter);
        return matchesFilterCriteria(field, criteria, fieldCropsMap);
      }
    }
  });
};
